import React from 'react';
import { CheckCircle, AlertCircle, Shield, RefreshCw, Target, Droplet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const iconMap = {
  'circle-dot': CheckCircle,
  'layers': Shield,
  'activity': RefreshCw,
  'cpu': Target,
  'droplet': Droplet,
  'disc': AlertCircle,
  'refresh-cw': RefreshCw,
  'filter': Target,
  'clipboard-check': CheckCircle,
  'wind': RefreshCw,
  'search': CheckCircle,
  'wrench': RefreshCw,
  'terminal': Target,
  'target': Target,
  'shield': Shield
};

// Modal for Gearbox Repair (with tabs for Types and Methods)
export const GearboxModal = ({ isOpen, onOpenChange, gearboxTypes, repairMethods, isEnglish }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-3xl font-bold text-slate-800">
            {isEnglish ? 'Automatic Transmissions' : 'Boîtes Automatiques'}
          </DialogTitle>
        </DialogHeader>
        
        <Tabs defaultValue="types" className="mt-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="types">
              {isEnglish ? 'Types of Gearboxes' : 'Types de Boîtes'}
            </TabsTrigger>
            <TabsTrigger value="methods">
              {isEnglish ? 'Repair Methods' : 'Méthodes de Réparation'}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="types" className="mt-6">
            <p className="text-lg text-slate-600 mb-8">
              {isEnglish 
                ? 'Discover the different transmission technologies, their operation and specific characteristics'
                : 'Découvrez les différentes technologies de transmission, leur fonctionnement et leurs caractéristiques'}
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              {gearboxTypes.map((gearbox) => {
                const GearboxIcon = iconMap[gearbox.icon] || CheckCircle;
                const advantages = isEnglish ? gearbox.advantagesEn : gearbox.advantages;
                const commonIssues = isEnglish ? gearbox.commonIssuesEn : gearbox.commonIssues;
                
                return (
                  <Card key={gearbox.id} className="overflow-hidden">
                    <div className="relative h-40 overflow-hidden">
                      <img 
                        src={gearbox.image}
                        alt={isEnglish ? gearbox.typeEn : gearbox.type}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent"></div>
                      
                      <div className="absolute top-3 left-3 w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center shadow-xl">
                        <GearboxIcon className="text-white" size={24} />
                      </div>

                      <div className="absolute bottom-3 left-3 right-3">
                        <h3 className="text-xl font-bold text-white">
                          {isEnglish ? gearbox.typeEn : gearbox.type}
                        </h3>
                      </div>
                    </div>

                    <CardContent className="p-4">
                      <p className="text-slate-600 mb-4 text-sm leading-relaxed">
                        {isEnglish ? gearbox.descriptionEn : gearbox.description}
                      </p>

                      <Accordion type="single" collapsible className="w-full">
                        <AccordionItem value="advantages">
                          <AccordionTrigger className="text-slate-800 font-semibold hover:text-red-600 text-sm">
                            <div className="flex items-center gap-2">
                              <CheckCircle size={16} className="text-green-600" />
                              {isEnglish ? 'Advantages' : 'Avantages'}
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            <ul className="space-y-1 mt-2">
                              {advantages.map((advantage, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-slate-600 text-sm">
                                  <CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
                                  <span>{advantage}</span>
                                </li>
                              ))}
                            </ul>
                          </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="issues">
                          <AccordionTrigger className="text-slate-800 font-semibold hover:text-red-600 text-sm">
                            <div className="flex items-center gap-2">
                              <AlertCircle size={16} className="text-orange-600" />
                              {isEnglish ? 'Common Issues' : 'Problèmes Courants'}
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            <ul className="space-y-1 mt-2">
                              {commonIssues.map((issue, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-slate-600 text-sm">
                                  <AlertCircle size={14} className="text-orange-600 mt-0.5 flex-shrink-0" />
                                  <span>{issue}</span>
                                </li>
                              ))}
                            </ul>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
          
          <TabsContent value="methods" className="mt-6">
            <p className="text-lg text-slate-600 mb-8">
              {isEnglish 
                ? 'Our professional repair methods for automatic transmissions'
                : 'Nos méthodes professionnelles de réparation des boîtes automatiques'}
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              {repairMethods.map((method) => {
                const MethodIcon = iconMap[method.icon] || RefreshCw;
                const steps = isEnglish ? method.stepsEn : method.steps;
                
                return (
                  <Card key={method.id} className="overflow-hidden border-2">
                    <CardHeader className="bg-gradient-to-r from-slate-50 to-white pb-3">
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
                          <MethodIcon className="text-white" size={24} />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-slate-800">
                            {isEnglish ? method.methodEn : method.method}
                          </h3>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="p-4">
                      <p className="text-slate-600 mb-4 text-sm leading-relaxed">
                        {isEnglish ? method.descriptionEn : method.description}
                      </p>

                      <div className="bg-slate-50 rounded-lg p-3">
                        <h4 className="text-sm font-semibold text-slate-700 mb-2">
                          {isEnglish ? 'Process:' : 'Processus :'}
                        </h4>
                        <ul className="space-y-1">
                          {steps.map((step, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-slate-600 text-sm">
                              <CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
                              <span>{step}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

// Generic modal for services with simple details
export const GenericServiceModal = ({ isOpen, onOpenChange, title, subtitle, details, isEnglish, type }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-3xl font-bold text-slate-800">
            {title}
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-6">
          <p className="text-lg text-slate-600 mb-8">
            {subtitle}
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {details.map((detail) => {
              const DetailIcon = iconMap[detail.icon] || CheckCircle;
              const advantages = detail.advantages || (isEnglish ? detail.advantagesEn : null);
              
              return (
                <Card key={detail.id} className="overflow-hidden border-2 hover:border-red-400 transition-colors">
                  <div className="relative h-40 overflow-hidden">
                    <img 
                      src={detail.image}
                      alt={isEnglish ? detail.titleEn : detail.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent"></div>
                    
                    <div className="absolute top-3 left-3 w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center shadow-xl">
                      <DetailIcon className="text-white" size={20} />
                    </div>
                  </div>

                  <CardContent className="p-4">
                    <h3 className="text-lg font-bold text-slate-800 mb-2">
                      {isEnglish ? detail.titleEn : detail.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-3">
                      {isEnglish ? detail.descriptionEn : detail.description}
                    </p>
                    
                    {advantages && (
                      <div className="bg-green-50 rounded-lg p-3 mt-3">
                        <h4 className="text-xs font-semibold text-green-800 mb-2">
                          {isEnglish ? 'Advantages:' : 'Avantages :'}
                        </h4>
                        <ul className="space-y-1">
                          {advantages.map((advantage, idx) => (
                            <li key={idx} className="flex items-start gap-1.5 text-slate-600 text-xs">
                              <CheckCircle size={12} className="text-green-600 mt-0.5 flex-shrink-0" />
                              <span>{advantage}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Modal for Maintenance (with includes)
export const MaintenanceModal = ({ isOpen, onOpenChange, maintenanceTypes, isEnglish }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-3xl font-bold text-slate-800">
            {isEnglish ? 'Types of Maintenance Services' : 'Types d\'Entretiens'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-6">
          <p className="text-lg text-slate-600 mb-8">
            {isEnglish 
              ? 'Discover our complete maintenance services to keep your vehicle in perfect condition'
              : 'Découvrez nos services d\'entretien complets pour maintenir votre véhicule en parfait état'}
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {maintenanceTypes.map((maintenance) => {
              const MaintenanceIcon = iconMap[maintenance.icon] || CheckCircle;
              const includes = isEnglish ? maintenance.includesEn : maintenance.includes;
              
              return (
                <Card key={maintenance.id} className="overflow-hidden border-2">
                  <CardHeader className="bg-gradient-to-r from-slate-50 to-white pb-3">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
                        <MaintenanceIcon className="text-white" size={24} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-slate-800">
                          {isEnglish ? maintenance.typeEn : maintenance.type}
                        </h3>
                        <p className="text-sm text-red-600 font-semibold mt-1">
                          {isEnglish ? maintenance.intervalEn : maintenance.interval}
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="p-4">
                    <p className="text-slate-600 mb-4 text-sm leading-relaxed">
                      {isEnglish ? maintenance.descriptionEn : maintenance.description}
                    </p>

                    <div className="bg-slate-50 rounded-lg p-3">
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">
                        {isEnglish ? 'Includes:' : 'Comprend :'}
                      </h4>
                      <ul className="space-y-1">
                        {includes.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-slate-600 text-sm">
                            <CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
